// updaters/AircraftUpdater.ts
import type { Map as MapType } from "maplibre-gl";
import type { MapUpdater, UpdaterContext } from "./types";
import type { SkopaTarget } from "../types";
import type { AircraftManager } from "../managers/aircraft/AircraftManager";
import { LAYERS } from "../layers/constants";

export class AircraftUpdater implements MapUpdater {
  readonly name = "aircraft";
  readonly topics = ["skopa/adsb", "skopa/adsb:batch", "skopa/aircraft:remove"];

  private aircraftManager: AircraftManager | null = null;
  private filterUnsubscribe: (() => void) | null = null;

  constructor(private ctx: UpdaterContext) {}

  async initialise(map: MapType) {
    console.log("[AircraftUpdater] Initializing...");
  }

  setAircraftManager(manager: AircraftManager | null) {
    if (this.filterUnsubscribe) {
      this.filterUnsubscribe();
      this.filterUnsubscribe = null;
    }

    this.aircraftManager = manager;
    if (this.aircraftManager) {
      this.filterUnsubscribe = this.aircraftManager.filterEngine.subscribe(
        () => {
          this.purgeFilteredCache();

          const mapInstance = this.ctx.map;
          if (!mapInstance) return;

          // Target BOTH potential layer allocations to guarantee the filter binds perfectly
          const targets = [
            LAYERS.AIRCRAFT,
            (LAYERS as any).AIRCRAFT_SYMBOLS,
          ].filter((layerId) => layerId && mapInstance.getLayer(layerId));

          if (targets.length === 0) return;

          try {
            const snapshot = manager?.filterEngine.getSnapshot();
            const hiddenCellsArray: string[] = snapshot?.hiddenCells || [];
            const totalHiddenCount = hiddenCellsArray.length;

            let nextFilter: any;

            if (totalHiddenCount >= 35) {
              nextFilter = [
                "==",
                ["coalesce", ["get", "matrix_cell"], ""],
                "force_hide_all_layer_assets",
              ];
            } else if (totalHiddenCount === 0) {
              nextFilter = null; // Clear completely if nothing is hidden
            } else {
              // Standard optimized MapLibre boolean array evaluation to prevent runtime crashes
              nextFilter = [
                "!",
                [
                  "in",
                  [
                    "coalesce",
                    ["get", "matrix_cell"],
                    [
                      "concat",
                      ["coalesce", ["get", "standard_identity"], "u"],
                      "-",
                      ["coalesce", ["get", "environment"], "a"],
                    ],
                  ],
                  ["literal", hiddenCellsArray],
                ],
              ];
            }

            // Apply cleanly across all captured track vector layers
            for (const layerId of targets) {
              mapInstance.setFilter(layerId, nextFilter);
            }
          } catch (e) {
            console.error(
              "[AircraftUpdater] Map memory-sync filter push failed:",
              e,
            );
          }
        },
      );
    }
  }

  async handleMessage(topic: string, payload: any) {
    if (!this.aircraftManager) return;

    switch (topic) {
      case "skopa/adsb":
        this.handleUpdate(payload as SkopaTarget);
        break;
      case "skopa/adsb:batch":
        this.handleBatchUpdate(payload as SkopaTarget[]);
        break;
      case "skopa/aircraft:remove":
        this.aircraftManager.removeAircraft(payload.track_id);
        break;
    }
  }

  private handleUpdate(target: SkopaTarget) {
    if (!this.aircraftManager) return;

    const filterEngine = this.aircraftManager.filterEngine;
    const identity = target.standard_identity || "u";
    const environment = target.environment || "a";

    if (!filterEngine.isCellVisible(identity, environment)) {
      this.aircraftManager.removeAircraft(target.track_id);
      return;
    }

    this.aircraftManager.updateAircraftBatch([target]);
  }

  private handleBatchUpdate(targets: SkopaTarget[]) {
    if (!this.aircraftManager) return;

    const filterEngine = this.aircraftManager.filterEngine;
    const validUpdates: SkopaTarget[] = [];

    for (const target of targets) {
      const identity = target.standard_identity || "u";
      const environment = target.environment || "a";

      if (!filterEngine.isCellVisible(identity, environment)) {
        this.aircraftManager.removeAircraft(target.track_id);
        continue;
      }
      validUpdates.push(target);
    }

    if (validUpdates.length > 0) {
      this.aircraftManager.updateAircraftBatch(validUpdates);
    }
  }

  private purgeFilteredCache() {
    if (!this.aircraftManager) return;

    const manager = this.aircraftManager;
    const filterEngine = manager.filterEngine;

    // Type-safe handling: Supports both standard JS Records and dynamic ES6 Map classes cleanly
    const targetsSource = manager.getAllTargets();
    const targetEntries =
      typeof (targetsSource as any).entries === "function"
        ? Array.from((targetsSource as any).entries())
        : Object.entries(targetsSource);

    let selectedStateAltered = false;

    for (const [track_id, target] of targetEntries as [string, AdsbTarget][]) {
      const identity = target.standard_identity || "u";
      const environment = target.environment || "a";

      if (!filterEngine.isCellVisible(identity, environment)) {
        if (manager.isSelected(track_id)) {
          selectedStateAltered = true;
        }
        manager.removeAircraft(track_id);
      }
    }

    if (selectedStateAltered) {
      manager.notifySelectedAircraftChanged();
    }
  }

  destroy() {
    if (this.filterUnsubscribe) {
      this.filterUnsubscribe();
      this.filterUnsubscribe = null;
    }
    this.aircraftManager = null;
  }
}
